"use client";

import React, { useEffect } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { DoubleArrowDownIcon } from "@radix-ui/react-icons";
import BigNumber from "bignumber.js";
import { motion } from "framer-motion";
import { Button, Skeleton } from "@heroui/react";

import { useGetAccountTokens } from "../../hooks/useGetAccountTokens";
import { useSwapTokens } from "../../hooks/ashswap/useSwapTokens";
import { useGetAshswapAggregation } from "../../hooks/ashswap/useGetAshswapAggregation";
import { A1X_TOKEN, EGLD } from "../../config/sharedConfig";
import { StyledNumberDisplay } from "../styled-number-display";
import { AssetImage } from "../AssetImage";
import type { BuyA1XSchema } from "../../validations/buyA1XSchema";
import type { TokenType } from "../../types/token.types";
import { ControlledTokenSelectModal } from "./ControlledTokenSelectModal";

interface Props {
  nextStep: () => void;
}

export const ChooseBuyTokens = ({ nextStep }: Props) => {
  const form = useFormContext<BuyA1XSchema>();
  const formValues = useWatch({
    control: form.control,
  });
  console.log("formValues", formValues);

  const { data: walletTokens } = useGetAccountTokens();
  const { tokens } = useSwapTokens(form.control as any); // Type assertion for compatibility
  const { data, isFetching, isLoading } = useGetAshswapAggregation(
    formValues.fromToken as any,
    A1X_TOKEN,
    formValues.amountDenominated,
    formValues.slippage
  );

  const isPriceFetching = isFetching || isLoading;

  // Get A1X token details
  const a1xToken = tokens?.find((token) => token.identifier === A1X_TOKEN);

  const fromTokenBalance = React.useMemo(() => {
    if (!formValues.fromToken || !walletTokens) return "0";
    const token = walletTokens.find(
      (walletToken: TokenType) =>
        walletToken.identifier === formValues.fromToken?.identifier
    );
    if (!token) return "0";
    return BigNumber(token.balance ?? "0")
      .dividedBy(10 ** token.decimals)
      .toFixed(6);
  }, [walletTokens, formValues.fromToken]);

  const handleReview = () => {
    if (
      BigNumber(fromTokenBalance).lt(formValues.amountDenominated ?? 0) ||
      BigNumber(fromTokenBalance).lte(0)
    ) {
      form.setError("amountDenominated", {
        message: "Insufficient balance",
      });
      return;
    }
    nextStep();
  };

  const handleMax = () => {
    let max = BigNumber(fromTokenBalance);
    if (formValues.fromToken?.identifier === EGLD) {
      max = max.gt(0.005) ? max.minus(0.005) : max;
    }
    form.setValue("amountDenominated", max.toFixed(6));
  };

  const handleTokenSelect = (token: TokenType) => {
    form.setValue("fromToken", {
      identifier: token.identifier,
      decimals: token.decimals,
      ticker: token.ticker,
      name: token.name,
      balance: token.balance,
    });
  };

  const getDisabledButtonText = () => {
    if (!formValues.amountDenominated) {
      return "Enter amount";
    }
    if (!formValues.fromToken) {
      return "Select token to sell";
    }
    return undefined;
  };

  useEffect(() => {
    form.clearErrors("amountDenominated");
  }, [formValues.amountDenominated]);

  return (
    <div className="flex flex-col gap-4">
      {/* From Token Section */}
      <div className="flex items-center justify-between p-4 bg-white/5 dark:bg-white/5 rounded-xl">
        <div className="flex flex-col gap-1 w-full items-start">
          <p className="text-sm text-gray-500 dark:text-gray-400">From</p>
          <div className="relative w-full">
            <input
              style={{
                fontSize: "1.5rem",
              }}
              type="number"
              inputMode="decimal"
              placeholder="0"
              className="bg-transparent border-none text-2xl font-bold w-full focus:outline-none text-black dark:text-white pr-16 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              value={formValues.amountDenominated || ""}
              onChange={(e) =>
                form.setValue("amountDenominated", e.target.value)
              }
            />
            <Button
              size="sm"
              variant="solid"
              onPress={handleMax}
              className="absolute right-0 top-1/2 transform -translate-y-1/2 h-5 px-2 bg-gray-500 dark:bg-content3 text-white dark:text-gray-200 hover:bg-gray-600 dark:hover:bg-gray-500 text-xs font-medium min-w-0"
            >
              MAX
            </Button>
          </div>
          <p
            className={`text-sm ${
              form.formState.errors.amountDenominated
                ? "text-red-500"
                : "text-gray-500 dark:text-gray-400"
            }`}
          >
            Balance: {fromTokenBalance}
          </p>
        </div>

        {/* Token Selector */}
        <div className="ml-4">
          <ControlledTokenSelectModal
            select={{
              tokens: React.useMemo(
                () =>
                  walletTokens
                    ?.filter((token: TokenType) => token.identifier !== A1X_TOKEN)
                    .map((token) => ({
                      identifier: token.identifier,
                      decimals: token.decimals,
                      price: token.price,
                    })) || [],
                [walletTokens]
              ),
              hiddenTokens: [A1X_TOKEN],
              name: "fromToken",
              placeholder: "Select Token",
              defaultValue: React.useMemo(
                () => ({
                  identifier: EGLD,
                  decimals: 18,
                }),
                []
              ),
            }}
          />
        </div>
      </div>

      {/* Arrow Icon */}
      <div className="flex justify-center">
        <div className="p-2 bg-white/5 dark:bg-white/5 rounded-full">
          <DoubleArrowDownIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
        </div>
      </div>

      {/* To Token Section (A1X - Fixed) */}
      <div className="flex items-center justify-between p-4 bg-white/5 dark:bg-white/5 rounded-xl">
        <div className="flex flex-col gap-1 items-start">
          <p className="text-sm text-gray-500 dark:text-gray-400">To</p>
          {isPriceFetching ? (
            <Skeleton className="w-32 h-10 rounded" />
          ) : (
            <p className="text-2xl font-bold text-black dark:text-white truncate max-w-[200px]" title={data?.sorResponse?.returnAmount || "0"}>
              {data?.sorResponse?.returnAmount || "0"}
            </p>
          )}
          <p className="text-sm text-gray-500 dark:text-gray-400">A1X Token</p>
        </div>

        <div className="ml-4 flex items-center gap-2 px-4 py-2 bg-white/10 dark:bg-white/10 rounded-lg">
          <AssetImage identifier={A1X_TOKEN} className="w-6 h-6" />
          <span className="text-black dark:text-white font-bold">A1X</span>
        </div>
      </div>

      {/* Review Button */}
      <motion.div className="w-full mt-4">
        <Button
          color="primary"
          className="w-full font-bold h-12 text-base"
          onPress={handleReview}
          disabled={
            isPriceFetching ||
            !formValues.amountDenominated ||
            !formValues.fromToken
          }
          isLoading={isPriceFetching}
        >
          {getDisabledButtonText() || "Review Purchase"}
        </Button>
      </motion.div>
    </div>
  );
};
